import { motion } from "motion/react";
import type { ComponentProps } from "react";

type AnimatedWandSparklesProps = Omit<ComponentProps<"svg">, "children"> & {
  isAnimating?: boolean;
};

export function AnimatedWandSparkles({
  className,
  isAnimating = false,
  ...props
}: AnimatedWandSparklesProps) {
  // Sparkle animation - scale and opacity pulse
  const sparkleAnimation = isAnimating
    ? {
        scale: [1, 1.4, 1],
        opacity: [0.6, 1, 0.6],
      }
    : {};

  const sparkleTransition = (delay: number) => ({
    duration: 1.2,
    repeat: Infinity,
    ease: "easeInOut" as const,
    delay,
  });

  // Wand bounce animation - single swing with bounce
  const wandAnimation = isAnimating
    ? {
        rotate: [0, -22, 2, 0],
      }
    : {};

  const wandTransition = {
    duration: 0.7,
    repeat: Infinity,
    repeatDelay: 1.8,
    ease: [0.34, 1.56, 0.64, 1] as const, // Custom spring-like easing
  };

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="-2 -2 28 28"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      {/* Wand group - rotates from the tip */}
      <motion.g
        animate={wandAnimation}
        transition={wandTransition}
        style={{ originX: "3px", originY: "21px" }}
      >
        {/* Main wand diagonal */}
        <path d="m21.64 3.64-1.28-1.28a1.21 1.21 0 0 0-1.72 0L2.36 18.64a1.21 1.21 0 0 0 0 1.72l1.28 1.28a1.2 1.2 0 0 0 1.72 0L21.64 5.36a1.2 1.2 0 0 0 0-1.72" />
        {/* Small line on wand */}
        <path d="m14 7 3 3" />
      </motion.g>

      {/* Star 1 - Left (cross pattern) */}
      <motion.g
        animate={sparkleAnimation}
        transition={sparkleTransition(0)}
        style={{ originX: "5px", originY: "8px" }}
      >
        <path d="M5 6v4" />
        <path d="M7 8H3" />
      </motion.g>

      {/* Star 2 - Right (cross pattern) */}
      <motion.g
        animate={sparkleAnimation}
        transition={sparkleTransition(0.4)}
        style={{ originX: "19px", originY: "16px" }}
      >
        <path d="M19 14v4" />
        <path d="M21 16h-4" />
      </motion.g>

      {/* Star 3 - Top small (cross pattern) */}
      <motion.g
        animate={sparkleAnimation}
        transition={sparkleTransition(0.8)}
        style={{ originX: "10px", originY: "3px" }}
      >
        <path d="M10 2v2" />
        <path d="M11 3H9" />
      </motion.g>
    </svg>
  );
}
